import { useContext, useEffect, useState } from "react"
import { Educator, User } from "../../mp-database"
import { Roles } from "../../mp-utils/generic-utils"
import { getFeedbackPrompt } from "../../../services/generic-services/feedback"
import { EventData, FeedbackPrompt } from "../../../types/gqlTypes/graphql"
import { useLocation, useParams } from "react-router-dom"
import { ProgramsContext } from "../../mp-shared-components/ready-shared-components/components/Navs"
import { getCurrentProductFromSessionStorage } from "../../mp-shared-components/ready-shared-components/components/Navs/ProductsDropDown/helpers"
import { getRandomInteger } from "../../mp-shared-components/generic-shared-components/helpers"
import { FEEDBACK_TIMESTAMP_KEY } from "./feedbackConstants"

const MIN = 1
const MAX = 3
const CHANCE = 1
const MIN_FEEDBACK_INTERVAL = 30 * (60 * 1000) // milliseconds

export enum FeedbackEvent {
  VIEW_REPORT = "View a Report",
  VIEW_STUDY_PLAN = "View a Study Plan",
  EDIT_STUDY_PLAN = "Edit a Study Plan",
  REVIEW_TEST_RESULTS = "Review Test Results",
  USE_MANAGE_MODULE = "Use Manage Module",
  COMPLETE_PRACTICE_TEST = "Complete a Practice Test",
  COMPLETE_SNAP_COURSE = "Complete a SnapCourse",
  COMPLETE_MASTERY_LESSON = "Complete a Mastery Lesson",
  COMPLETE_STUDY_PLAN = "Complete A Study Plan",
  COMPLETE_ALL_STUDY_PLANS = "Complete All Study Plans",
  USER_INITIATED_FEEDBACK = "User-Initiated Feedback"
}

const FEEDBACK_EVENT_ROLES = {
  "View a Report": Roles.Teacher,
  "View a Study Plan": Roles.Teacher,
  "Edit a Study Plan": Roles.Teacher,
  "Review Test Results": Roles.Teacher,
  "Use Manage Module": Roles.Teacher,
  "Complete a Practice Test": Roles.Student,
  "Complete a SnapCourse": Roles.Student,
  "Complete a Mastery Lesson": Roles.Student,
  "Complete A Study Plan": Roles.Student,
  "Complete All Study Plans": Roles.Student,
  "Sitewide Feedback": ""
}

const checkEventRole = (user: User, event: FeedbackEvent): boolean => {
  const feedbackEventRole = FEEDBACK_EVENT_ROLES[event]

  // Empty event role means event is available for any role.
  if (!feedbackEventRole) return true

  if (feedbackEventRole !== Roles.Teacher) {
    return user.role === feedbackEventRole
  }

  // If event role is "Teacher", it is available for "teacher" school admins too.
  return user.role === Roles.Teacher || (user as Educator).isTeacher
}

const shouldRequestFeedback = (user: User, event: FeedbackEvent): boolean => {
  if (!user || !event) {
    return false
  }

  // User-initiated event doesn't have limits of feedback
  if (event === FeedbackEvent.USER_INITIATED_FEEDBACK) return true

  // Check if user has correct role for the event.
  if (!checkEventRole(user, event)) {
    return false
  }

  // Roll for probability.
  const roll = getRandomInteger(MIN, MAX)

  if (roll > CHANCE) {
    return false
  }

  // Check last feedback timestamp.
  const lastFeedbackTimestamp = localStorage.getItem(
    `${FEEDBACK_TIMESTAMP_KEY}_${user.id}`
  )

  // No timestamp means we haven't asked for a feedback from this user yet.
  if (!lastFeedbackTimestamp) {
    return true
  }

  // Check that last feedback request was no less than [interval] ago.
  return Date.now() - Number(lastFeedbackTimestamp) >= MIN_FEEDBACK_INTERVAL
}

const setFeedbackTimestampKey = (userId: string, event: FeedbackEvent) => {
  if (!userId || !event) return

  // User-initiated event doesn't have limits of feedback
  if (event === FeedbackEvent.USER_INITIATED_FEEDBACK) return

  localStorage.setItem(
    `${FEEDBACK_TIMESTAMP_KEY}_${userId}`,
    Date.now().toString()
  )
}

const useFeedbackEventData = (
  currentProgram: string,
  testName: string,
  courseName: string
): EventData => {
  const location = useLocation()
  const params = useParams() as any

  const currentProduct = getCurrentProductFromSessionStorage()

  return {
    url: location.pathname,
    module: params?.module || null,
    product: currentProduct || params?.page || null,
    program: currentProgram || params?.program || null,
    course: courseName || params?.subject || null,
    item: testName || null
  }
}

type UseFeedbackRequest = {
  loading: boolean
  feedbackPrompt?: FeedbackPrompt
}

type FeedbackRequestAdditionalProperties = {
  testName?: string
  courseName?: string
  triggerRequestFlag?: boolean
  successfulAction?: () => void
  failedAction?: () => void
}

export const useFeedbackRequest = (
  user: User,
  event: FeedbackEvent,
  additionalProperties?: FeedbackRequestAdditionalProperties
): UseFeedbackRequest => {
  const {
    testName = null,
    courseName = null,
    triggerRequestFlag = true,
    successfulAction,
    failedAction
  } = additionalProperties || {}

  const { currentProgram } = useContext(ProgramsContext)

  const [loading, setLoading] = useState<boolean>(false)
  const [feedbackPrompt, setFeedbackPrompt] = useState<FeedbackPrompt>(null)

  const eventData = useFeedbackEventData(currentProgram, testName, courseName)

  useEffect(() => {
    if (!triggerRequestFlag) return

    setLoading(true)

    const shouldRequest = shouldRequestFeedback(user, event)

    if (!shouldRequest) return

    getFeedbackPrompt({ event, eventData })
      .then(feedbackPrompt => {
        setFeedbackPrompt(feedbackPrompt)

        setFeedbackTimestampKey(user?.id, event)

        successfulAction?.()
      })
      .catch(error => {
        console.warn("[useFeedbackRequest]", error)

        failedAction?.()
      })
      .finally(() => setLoading(false))
  }, [user, event, triggerRequestFlag])

  return { loading, feedbackPrompt }
}
